// src/Components/Drawer/PhotosTab/PhotosTab.js

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal, flushSync } from 'react-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import heic2any from 'heic2any';
import SortableImageItem from './SortableImageItem';
import RichTextEditor from '../../../utils/RichTextEditor';
import { ref, deleteObject } from 'firebase/storage';
import { doc, updateDoc, setDoc, deleteField } from 'firebase/firestore';
import { db, storage } from '../../../Firebase/firebase';
import ConfirmDialog from '../../ConfirmDialog/ConfirmDialog';

const PhotosTab = ({
  activeTab,
  selectedMarker,
  triggerAdd,
  triggerDelete,
  items,
  setItems,
  descriptions,
  setDescriptions,
  selectedIds,
  setSelectedIds,
  lastSelectedId,
  setLastSelectedId,
  isSaving,
  panelLeft = '60px',
  panelWidth = 'calc(100% - 60px)',
  isFocused = false,
  panelZIndex = 5,
  isAppFullScreen = false,
  displayPanel = null,
  onInteract = () => {},
  rteFontType = 'Arial',
  rteFontSize = '11pt',
}) => {
  // --- STATE ---
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // ALERT STATE
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [alert, setAlert] = useState({ visible: false, severity: 'info', message: '' });

  // --- REFS ---
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const fileInputRef = useRef(null);
  const gridRef = useRef(null); // Used for ResizeObserver
  const imagePanelRef = useRef(null); // Used for auto-scrolling

  const prevDeleteTriggerRef = useRef(triggerDelete);
  const prevAddTriggerRef = useRef(triggerAdd);

  // Zero-Latency Synchronous Refs
  const descriptionsRef = useRef(descriptions);
  descriptionsRef.current = descriptions;

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const setDescriptionsRef = useRef(setDescriptions);
  setDescriptionsRef.current = setDescriptions;

  const setItemsRef = useRef(setItems);
  setItemsRef.current = setItems;

  // Accumulate history synchronously during render
  const lastKnownDescriptionsRef = useRef({});
  lastKnownDescriptionsRef.current = {
    ...lastKnownDescriptionsRef.current,
    ...descriptions,
  };

  // --- DERIVED STATE ---
  const isSingleSelection = selectedIds.size === 1;
  const singleSelectedId = isSingleSelection ? [...selectedIds][0] : null;
  const activeDescription = singleSelectedId ? descriptions[singleSelectedId] || '' : '';

  // The Interaction Lock. True only if the user actively focused on the editor.
  const editorInteractedRef = useRef(false);

  useEffect(() => {
    editorInteractedRef.current = false;
  }, [singleSelectedId]);

  let editorMessage = 'No photo selected. Click on a photo to add text.';
  if (selectedIds.size > 1) {
    editorMessage = 'Select only one photo at a time to edit text.';
  }

  const showTempAlert = (severity, message) => {
    setAlert({ visible: true, severity, message });
    setTimeout(() => setAlert({ visible: false, severity: 'info', message: '' }), 2000);
  };

  // --- AUTO-SELECT FIRST PHOTO LOGIC ---
  useEffect(() => {
    if (activeTab === 'PHOTOS' && items.length > 0 && selectedIds.size === 0) {
      setSelectedIds(new Set([items[0].id]));
      setLastSelectedId(items[0].id);
    }
  }, [activeTab, items, selectedIds.size, setSelectedIds, setLastSelectedId]);

  // --- AUTO-SCROLL LOGIC ---
  useEffect(() => {
    if (imagePanelRef.current && items.length > 0) {
      // Delay allows the DOM to render the new grid items before measuring the scroll height
      setTimeout(() => {
        if (imagePanelRef.current) {
          imagePanelRef.current.scrollTo({
            top: imagePanelRef.current.scrollHeight,
            behavior: 'smooth',
          });
        }
      }, 150);
    }
  }, [items.length]);

  // --- HANDLE DESCRIPTION UPDATE ---
  const handleDescriptionUpdate = useCallback((targetId, htmlContent) => {
    if (!editorInteractedRef.current) {
      return;
    }

    if (targetId) {
      const currentDesc = lastKnownDescriptionsRef.current[targetId] || '';

      const normalizeHtmlForCompare = (html) => {
        if (!html) return '';
        try {
          const doc = new DOMParser().parseFromString(String(html), 'text/html');

          let cleaned = String(html)
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ');

          cleaned = cleaned.replace(/[\u200B-\u200D\uFEFF]/g, '');

          const text = doc.body.textContent.replace(/[\u200B-\u200D\uFEFF]/g, '') || '';

          if (text.trim() === '' && !doc.querySelector('img, video, audio')) {
            return '';
          }

          if (!/<\/?(p|br|div|h[1-6]|ul|ol|li|table|blockquote)[^>]*>/i.test(cleaned)) {
            cleaned = cleaned.replace(/\r?\n/g, '<br>');
            cleaned = `<p>${cleaned}</p>`;
          }

          cleaned = cleaned.replace(/<p><\/p>/gi, '');
          cleaned = cleaned.replace(/margin-bottom:\s*[^;"]+;?/gi, '');
          cleaned = cleaned.replace(/margin-top:\s*[^;"]+;?/gi, '');
          cleaned = cleaned.replace(/line-height:\s*[^;"]+;?/gi, '');

          cleaned = cleaned.replace(/style="\s*"/gi, '');
          cleaned = cleaned.replace(/style='\s*'/gi, '');
          cleaned = cleaned.replace(/\/>/g, '>');

          cleaned = cleaned.replace(/[\s;"']/g, '').toLowerCase();

          return cleaned;
        } catch (e) {
          return String(html)
            .trim()
            .replace(/[\s;"']/g, '')
            .toLowerCase();
        }
      };

      const cleanNew = normalizeHtmlForCompare(htmlContent);
      const cleanOld = normalizeHtmlForCompare(currentDesc);

      if (cleanNew !== cleanOld) {
        setDescriptionsRef.current((prev) => ({ ...prev, [targetId]: htmlContent }));
        setItemsRef.current([...itemsRef.current], true);
        lastKnownDescriptionsRef.current[targetId] = htmlContent;
      }
    }
  }, []);

  // --- FILE CHANGE HANDLER ---
  const handleFileChange = async (event) => {
    const rawFiles = event.target.files;
    if (!rawFiles || rawFiles.length === 0) return;

    try {
      flushSync(() => {
        setIsProcessing(true);
      });
    } catch (e) {
      setIsProcessing(true);
    }

    const filesArray = Array.from(rawFiles);

    setTimeout(async () => {
      try {
        const processedFiles = await Promise.all(
          filesArray.map(async (file) => {
            if (
              file.name.toLowerCase().endsWith('.heic') ||
              file.name.toLowerCase().endsWith('.heif')
            ) {
              try {
                const convertedBlob = await heic2any({
                  blob: file,
                  toType: 'image/jpeg',
                  quality: 0.8,
                });
                const blob = Array.isArray(convertedBlob)
                  ? convertedBlob[0]
                  : convertedBlob;
                return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
                  type: 'image/jpeg',
                });
              } catch (error) {
                console.error('HEIC conversion failed:', error);
                showTempAlert('error', `Could not convert ${file.name}`);
                return null;
              }
            }
            return file;
          }),
        );

        const validFiles = processedFiles.filter((f) => f !== null);

        const newItems = validFiles.map((file, index) => {
          const isVideo = file.type.startsWith('video/');
          const isAudio = file.type.startsWith('audio/');
          let type = 'image';
          if (isVideo) type = 'video';
          if (isAudio) type = 'audio';

          return {
            id: `${Date.now()}-${Math.random()}-${index}`,
            img: URL.createObjectURL(file),
            title: file.name,
            file: file,
            type: type,
          };
        });

        setItems([...items, ...newItems], true);

        if (newItems.length > 0) {
          const firstNewId = newItems[0].id;
          setSelectedIds(new Set([firstNewId]));
          setLastSelectedId(firstNewId);
        }
      } catch (err) {
        console.error('Error processing files:', err);
        showTempAlert('error', 'Error processing files.');
      } finally {
        setIsProcessing(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    }, 300);
  };

  // --- ADD LISTENER ---
  useEffect(() => {
    if (triggerAdd && triggerAdd > 0 && triggerAdd !== prevAddTriggerRef.current) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      fileInputRef.current && fileInputRef.current.click();
      prevAddTriggerRef.current = triggerAdd;
    }
  }, [triggerAdd]);

  // --- DELETE LISTENER ---
  useEffect(() => {
    if (
      triggerDelete &&
      triggerDelete > 0 &&
      triggerDelete !== prevDeleteTriggerRef.current
    ) {
      if (selectedIds.size === 0) showTempAlert('info', 'Select photos to delete.');
      else setShowDeleteConfirm(true);
      prevDeleteTriggerRef.current = triggerDelete;
    }
  }, [triggerDelete, selectedIds]);

  // --- DELETION LOGIC ---
  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    setIsProcessing(true);

    try {
      const currentSelectedIndex = items.findIndex((item) => selectedIds.has(item.id));
      const removedItems = items.filter((item) => selectedIds.has(item.id));

      const groupId = selectedMarker.markerGroupID || selectedMarker.id;

      removedItems.forEach((item) => {
        if (item.img && item.img.startsWith('blob:')) {
          URL.revokeObjectURL(item.img);
        }
      });

      for (const delItem of removedItems) {
        if (!delItem.file && delItem.img && delItem.img.startsWith('http')) {
          try {
            const storagePath = `photos/${groupId}/${delItem.id}_${delItem.title}`;
            const deleteRef = ref(storage, storagePath);
            await deleteObject(deleteRef);
          } catch (err) {
            if (err.code !== 'storage/object-not-found') {
              console.warn('[PhotosTab] Storage delete warning:', err);
            }
          }
        }
      }

      const remainingItems = items.filter((item) => !selectedIds.has(item.id));

      if (!selectedMarker.isNew) {
        const firestoreData = remainingItems.map((item) => ({
          id: item.id,
          url: item.img,
          description: descriptions[item.id] || '',
          fileName: item.title || 'Untitled',
          uploadedAt: new Date().toISOString(),
          type: item.type || 'image',
        }));

        const docRef = doc(db, 'markerGroups', groupId);
        await setDoc(
          docRef,
          {
            photos: firestoreData,
            lastModified: new Date().toISOString(),
          },
          { merge: true },
        );

        try {
          const type = (selectedMarker.type || 'marker').toLowerCase();
          let collectionName = 'markers';
          if (type === 'polygon') collectionName = 'polygons';
          if (type === 'line' || type === 'polyline') collectionName = 'polylines';

          const geomRef = doc(db, collectionName, selectedMarker.id);
          await updateDoc(geomRef, {
            photos: deleteField(),
          });
        } catch (legacyErr) {
          // Silent catch
        }
      }

      const shouldMarkDirty = !!selectedMarker.isNew;
      setItems(remainingItems, shouldMarkDirty);

      const newDescriptions = { ...descriptions };
      selectedIds.forEach((id) => delete newDescriptions[id]);
      setDescriptions(newDescriptions);

      if (remainingItems.length > 0) {
        let newIndex = Math.max(
          0,
          Math.min(currentSelectedIndex, remainingItems.length - 1),
        );
        const newIdToSelect = remainingItems[newIndex].id;
        setSelectedIds(new Set([newIdToSelect]));
        setLastSelectedId(newIdToSelect);
      } else {
        setSelectedIds(new Set());
        setLastSelectedId(null);
      }

      showTempAlert('success', 'Items deleted successfully.');
    } catch (error) {
      console.error('[PhotosTab] Delete Failed:', error);
      showTempAlert('error', 'Failed to delete items.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePhotoClick = (event, id) => {
    event.stopPropagation();
    const { shiftKey, ctrlKey, metaKey } = event;

    if (shiftKey && lastSelectedId) {
      const index = items.findIndex((item) => item.id === id);
      const lastIndex = items.findIndex((item) => item.id === lastSelectedId);
      if (lastIndex !== -1 && index !== -1) {
        const start = Math.min(lastIndex, index);
        const end = Math.max(lastIndex, index);
        const rangeSet = new Set();
        for (let i = start; i <= end; i++) rangeSet.add(items[i].id);
        setSelectedIds(rangeSet);
        return;
      }
    }

    if (ctrlKey || metaKey) {
      const newSet = new Set(selectedIds);
      if (newSet.has(id)) newSet.delete(id);
      else {
        newSet.add(id);
        setLastSelectedId(id);
      }
      setSelectedIds(newSet);
      return;
    }

    setSelectedIds(new Set([id]));
    setLastSelectedId(id);
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null) {
      const _items = [...items];
      const dragged = _items.splice(dragItem.current, 1)[0];
      _items.splice(dragOverItem.current, 0, dragged);
      setItems(_items, true);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const portalOverlay = createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        bgcolor: 'rgba(255,255,255,0.1)',
        zIndex: 100010,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'wait',
      }}>
      <CircularProgress sx={{ color: '#00B0FF' }} />
    </Box>,
    document.body,
  );

  // --- DYNAMIC DOM MEASUREMENT ---
  const [firstRowHeight, setFirstRowHeight] = useState(0);

  useEffect(() => {
    if (!gridRef.current || items.length === 0) return;
    const firstChild = gridRef.current.children[0];
    if (!firstChild) return;

    const observer = new ResizeObserver((entries) => {
      if (entries[0]) {
        const height = entries[0].contentRect.height;
        setFirstRowHeight(height);
      }
    });

    observer.observe(firstChild);
    return () => observer.disconnect();
  }, [items.length, isAppFullScreen, isFocused, panelWidth]);

  const isVisible =
    displayPanel !== null ? displayPanel : activeTab === 'PHOTOS' || isAppFullScreen;

  // EMPTY STATE RENDER: Render a solid column if selectedMarker is null so the fullscreen layout doesn't break
  if (!selectedMarker) {
    return (
      <Box
        onPointerDownCapture={onInteract}
        sx={{
          containerType: 'inline-size', // Establishes container for cqw math
          display: isVisible ? 'block' : 'none',
          position: 'absolute',
          top: '45px',
          left: panelLeft,
          width: panelWidth,
          height: 'calc(100% - 45px)',
          zIndex: panelZIndex,
          borderRight: isAppFullScreen && !isFocused ? '1px solid #ccc' : 'none',
          backgroundColor: '#fff',
          boxShadow: isFocused
            ? '0px 0px 20px 5px rgba(0,0,0,0.4)'
            : '4px 4px 10px rgba(0,0,0,0.5)',
          pointerEvents: 'auto',
          transition: 'all 0.3s ease',
        }}>
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography
            sx={{ color: 'text.secondary', fontStyle: 'italic', fontSize: '0.85rem' }}>
            Click on a marker to add photos and clips.
          </Typography>
        </Box>
      </Box>
    );
  }

  // --- HEIGHT RATIO LOGIC ---
  const collapsedHeight = 'calc(35% - 10px)';

  // Expanded Height calculation based on exact pixel math.
  // 16px Top Padding + 12px Bottom Padding = 28px Total Padding
  // Setting the offset to 28px perfectly aligns the container bottom with the top of the grid gap.
  const expandedHeight =
    firstRowHeight > 0 ? `calc(100% - ${firstRowHeight + 28}px)` : '65%'; // Fallback before observer fires

  const activeHeight = isDescriptionExpanded ? expandedHeight : collapsedHeight;

  return (
    <>
      {(isProcessing || isSaving) && portalOverlay}

      <ConfirmDialog
        open={alert.visible}
        message={alert.message}
        severity={alert.severity}
        sx={{ zIndex: 100005 }}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleConfirmDelete}
        message='Delete selected items permanently?'
        severity='warning'
        sx={{ zIndex: 100005 }}
      />

      <input
        type='file'
        multiple
        accept='image/*, .heic, .heif, video/mp4, audio/mp3, audio/mpeg, audio/wav, audio/x-wav, audio/mp4, audio/x-m4a, audio/aac'
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* FULL WRAPPER FOR DYNAMIC POSITIONING AND FOCUS SHADOW */}
      <Box
        onPointerDownCapture={onInteract}
        sx={{
          containerType: 'inline-size', // Establishes container for cqw math
          display: isVisible ? 'block' : 'none',
          position: 'absolute',
          top: '45px',
          left: panelLeft,
          width: panelWidth,
          height: 'calc(100% - 45px)',
          zIndex: panelZIndex,
          borderRight: isAppFullScreen && !isFocused ? '1px solid #ccc' : 'none',
          backgroundColor: '#fff',
          boxShadow: isFocused
            ? '0px 0px 20px 5px rgba(0,0,0,0.4)'
            : '4px 4px 10px rgba(0,0,0,0.5)',
          pointerEvents: 'auto',
          transition: 'all 0.3s ease',
        }}>
        {/* 1. IMAGE GRID PANEL (Top Layer) */}
        <Box
          className='image-panel'
          ref={imagePanelRef}
          sx={{
            display: 'block',
            position: 'absolute',
            top: 0,
            bottom: activeHeight,
            left: 0,
            width: '100%',
            backgroundColor: '#e1f5fe',
            pointerEvents: 'auto',
            overflowY: 'auto',
            overflowX: 'hidden',
            transition: 'bottom 0.3s ease',
            // THE FIX: Decoupling padding to prevent overlapping with the grid gap
            pt: items.length > 0 ? 2 : 0,
            px: items.length > 0 ? 2 : 0,
            pb: items.length > 0 ? '12px' : 0,
            zIndex: 2,
          }}
          onClick={() => {
            if (items.length > 0) {
              setSelectedIds(new Set([items[0].id]));
              setLastSelectedId(items[0].id);
            }
          }}>
          {items.length === 0 ? (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Typography
                variant='h5'
                sx={{ color: '#000', fontStyle: 'italic', opacity: 0.5 }}>
                No Media
              </Typography>
            </Box>
          ) : (
            <Box
              ref={gridRef}
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '12px',
                width: '100%',
              }}>
              {items.map((item, index) => (
                <SortableImageItem
                  key={item.id}
                  item={item}
                  index={index}
                  handleClick={handlePhotoClick}
                  isSelected={selectedIds.has(item.id)}
                  handleDragStart={(e, idx) => {
                    dragItem.current = idx;
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  handleDragEnter={(e, idx) => {
                    dragOverItem.current = idx;
                  }}
                  handleDragEnd={handleDragEnd}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* 2. DESCRIPTION PANEL (Bottom Layer) */}
        <Box
          className='description-panel'
          sx={{
            display: 'block',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: activeHeight,
            backgroundColor: '#fff',
            pointerEvents: 'auto',
            borderTop: '1px solid #e0e0e0',
            transition: 'height 0.3s ease',
            zIndex: 10, // Creates a strict stacking context to trap the text editor layers
          }}>
          <Box
            onFocusCapture={() => {
              editorInteractedRef.current = true;
            }}
            onClickCapture={() => {
              editorInteractedRef.current = true;
            }}
            sx={{
              width: '100%',
              height: '100%',
              overflowY: isDescriptionExpanded ? 'auto' : 'hidden',
              p: 2,
            }}>
            <RichTextEditor
              key={singleSelectedId || 'no-selection'}
              initialContent={activeDescription}
              onUpdate={(html) => handleDescriptionUpdate(singleSelectedId, html)}
              readOnly={!isSingleSelection}
              readOnlyMessage={editorMessage}
              defaultFontType={rteFontType}
              defaultFontSize={rteFontSize}
            />
          </Box>
        </Box>

        {/* 3. EXPAND BUTTON SIBLING */}
        {items.length > 0 && (
          <Button
            className='see-more-btn'
            onClick={(e) => {
              e.stopPropagation();
              setIsDescriptionExpanded(!isDescriptionExpanded);
            }}
            variant='contained'
            size='small'
            sx={{
              position: 'absolute',
              bottom: 10,
              right: 45, // Adjusted to avoid scrollbar overlap
              zIndex: 15, // Safely above the description panel (zIndex: 10)
              pointerEvents: 'auto',
              textTransform: 'none',
              backgroundColor: 'rgba(0,0,0,0.6)',
              color: '#fff',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
            }}>
            {isDescriptionExpanded ? 'See less ...' : 'See more ...'}
          </Button>
        )}
      </Box>
    </>
  );
};

export default PhotosTab;
